// Sugalarm — battery bridge wrapper (cycle 229).
//
// The single owner of access to the native `Battery` plugin. That plugin
// exists ONLY in the Android kiosk APK (the `kiosk` product flavor); it is
// absent on web, the Pi kiosk, iPhone, the phone layout, and the `standard`
// Android flavor — so `isAvailable()` is false on every surface except the
// kiosk APK, and the header battery indicator gates strictly on it.
//
// Like every native plugin in the pure-remote webview (cycle 26/138/197), it
// is reached through the bridge-injected `window.Capacitor.Plugins` proxy —
// NEVER a bare `import("@capacitor/...")`. Mirrors `lib/screenBrightness.ts`.
//
// READ-ONLY: reports the device battery level + charging state. No write, no
// network, no secret, no PHI, no new permission.

import { isCapacitorNative } from "./capacitor";

export interface BatteryStatus {
  /** 0–100, or -1 when genuinely unreadable (the indicator renders nothing). */
  level: number;
  charging: boolean;
}

interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

interface BatteryProxy {
  getStatus(): Promise<BatteryStatus>;
  addListener(
    event: "batteryStatus",
    cb: (s: BatteryStatus) => void,
  ): Promise<PluginListenerHandle> | PluginListenerHandle;
}

/** Resolve the natively-registered plugin proxy off the Capacitor bridge,
 *  or null on any surface where it isn't present. */
function plugin(): BatteryProxy | null {
  if (!isCapacitorNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.Battery;
  return p != null ? (p as BatteryProxy) : null;
}

/** True iff the native Battery plugin is registered on the bridge — i.e. the
 *  SPA is running inside the Android kiosk APK (the tablet). The header
 *  battery indicator renders only when this is true. */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Read the current battery status, or null on a non-kiosk-APK surface / on
 *  any bridge error. */
export async function getStatus(): Promise<BatteryStatus | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.getStatus();
  } catch (err) {
    console.warn("[battery] getStatus failed", err);
    return null;
  }
}

/** Subscribe to live battery changes; returns an unsubscribe. No-op (returns
 *  a no-op cleanup) when the plugin isn't available. */
export function subscribe(cb: (s: BatteryStatus) => void): () => void {
  const p = plugin();
  if (p == null) return () => {};
  let handle: PluginListenerHandle | null = null;
  let cancelled = false;
  try {
    // addListener may return the handle directly or a Promise of it; handle
    // both, and remove immediately if we were already unsubscribed.
    Promise.resolve(p.addListener("batteryStatus", cb))
      .then((h) => {
        if (cancelled) {
          try {
            void h?.remove?.();
          } catch {
            /* ignore */
          }
        } else {
          handle = h;
        }
      })
      .catch((err) => console.warn("[battery] addListener failed", err));
  } catch (err) {
    console.warn("[battery] subscribe failed", err);
  }
  return () => {
    cancelled = true;
    if (handle) {
      try {
        void handle.remove();
      } catch {
        /* ignore */
      }
      handle = null;
    }
  };
}
